import { useEffect, useMemo, useState } from "react";
import { DEFAULT_TOR_RULES_FORM } from "./constants";
import { mapSourceToTorRulesFormValues } from "./mapTorRulesToFormValues";
import type { TorRulesFormValues, TorRulesHydrationSource } from "./types";

const previousTermSnapshotsByLeaseId = new Map<string, TorRulesFormValues>();

type UsePreviousTermTorRulesSnapshotProps = {
  leaseId?: string;
  source?: TorRulesHydrationSource;
};

const hasParentTorSource = (source?: TorRulesHydrationSource) =>
  source?.parentTorRules !== undefined || source?.parentTorRuleCondition !== undefined;

export const usePreviousTermTorRulesSnapshot = (props: UsePreviousTermTorRulesSnapshotProps) => {
  const { leaseId, source } = props;
  const usesParentTorFields = hasParentTorSource(source);

  const parentTermValues = useMemo(() => {
    if (!usesParentTorFields) return DEFAULT_TOR_RULES_FORM;

    return mapSourceToTorRulesFormValues({
      torRules: source?.parentTorRules,
      torRuleCondition: source?.parentTorRuleCondition,
    });
  }, [source?.parentTorRuleCondition, source?.parentTorRules, usesParentTorFields]);

  const torRules = source?.torRules;
  const torRuleCondition = source?.torRuleCondition;

  const [snapshot, setSnapshot] = useState<TorRulesFormValues>(() => {
    if (leaseId && previousTermSnapshotsByLeaseId.has(leaseId)) {
      return previousTermSnapshotsByLeaseId.get(leaseId)!;
    }
    return DEFAULT_TOR_RULES_FORM;
  });

  const hasLegacySnapshot = Boolean(leaseId && previousTermSnapshotsByLeaseId.has(leaseId));

  useEffect(() => {
    if (usesParentTorFields) return;
    if (!leaseId || previousTermSnapshotsByLeaseId.has(leaseId)) return;
    if (!torRules?.length) return;

    const captured = mapSourceToTorRulesFormValues({ torRules, torRuleCondition });
    previousTermSnapshotsByLeaseId.set(leaseId, captured);
    setSnapshot(captured);
  }, [leaseId, torRuleCondition, torRules, usesParentTorFields]);

  if (usesParentTorFields) {
    return {
      previousTermValues: parentTermValues,
      hasPreviousRules: Boolean(source?.parentTorRules?.length),
    };
  }

  return { previousTermValues: snapshot, hasPreviousRules: hasLegacySnapshot };
};
